import { useId, type ReactNode } from "react";
import { cn } from "../../utils";
import { Card, type CardProps } from "./card";

export interface SectionCardProps extends Omit<CardProps, "title"> {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  bodyClassName?: string;
  headerClassName?: string;
  density?: "comfortable" | "compact";
  headingLevel?: 2 | 3;
}

export function SectionCard({
  title,
  description,
  icon,
  actions,
  footer,
  bodyClassName,
  headerClassName,
  density = "comfortable",
  headingLevel = 2,
  elevation = "none",
  className,
  children,
  paddingMode = "bleed",
  ...props
}: SectionCardProps) {
  const titleId = `${useId()}-title`;
  const horizontalPadding =
    density === "compact"
      ? "px-surface-compact-x"
      : "px-surface-comfortable-x";
  const headerPadding =
    density === "compact"
      ? "pt-surface-compact-y"
      : "pt-surface-comfortable-y";
  const bodyPadding =
    density === "compact"
      ? "pb-surface-compact-y pt-4"
      : "pb-surface-comfortable-y pt-5";
  const Heading = headingLevel === 3 ? "h3" : "h2";

  return (
    <Card
      aria-labelledby={titleId}
      className={className}
      elevation={elevation}
      paddingMode={paddingMode}
      {...props}
    >
      <div
        className={cn(
          "flex flex-wrap items-start justify-between gap-4",
          horizontalPadding,
          headerPadding,
          headerClassName,
        )}
      >
        <div className="flex min-w-0 items-start gap-4">
          {icon ? (
            <div className="flex shrink-0 items-center justify-center border-l-2 border-border-accent py-1 pl-3 text-content-accent">
              {icon}
            </div>
          ) : null}
          <div className="min-w-0">
            <Heading
              id={titleId}
              className="font-display text-heading-md font-semibold text-content-primary"
            >
              {title}
            </Heading>
            {description ? (
              <p className="mt-1 text-body text-content-muted">{description}</p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            {actions}
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          horizontalPadding,
          bodyPadding,
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer ? (
        <div
          className={cn(
            "border-t border-border-subtle bg-surface-secondary/80 py-surface-compact-y",
            horizontalPadding,
          )}
        >
          {footer}
        </div>
      ) : null}
    </Card>
  );
}
